/**
 * CC-1c — typed discriminated audit-operation taxonomy.
 *
 * # Why this exists (ТЗ §14, full-stack gap-closure §3 CC-1c)
 *
 * Every audited command flows through the `pushAuditEvent` chokepoint, which
 * historically stored only a lean string payload (id / sequence / commandType
 * / source / actorEmail / summary). ТЗ §14 ("clicks/commands/params/errors/
 * timings") needs the structured PARAMS of each operation so the CC-1a server
 * channel can aggregate by parameter, not just by command name.
 *
 * This module is that structured layer. It is intentionally NOT a string bag:
 * `describeAuditOperation` is keyed on `command.type` (a discriminated union)
 * and returns a typed `TelemetryParams` carrying the salient scalar fields of
 * the operation. `categoryForCommandType` assigns every command literal a
 * stable, machine-groupable category — the `auditTaxonomy.test.ts` coverage
 * test enumerates every literal in `editorCommandSchema` +
 * `dynamicBlockCommandSchema` and asserts none falls through to `"other"`, so
 * no command can silently lack a payload.
 *
 * View operations (REGEN/REGENALL/ZOOM/PAN) do not pass through the command
 * schema — `describeViewOpOperation` builds their typed payload directly. The
 * `kind:"view-op"` discriminant is recovered downstream by the transport's
 * `classifyTelemetryKind` (anchored on the same four opType literals).
 *
 * Geometry is NEVER touched here — this module is pure observability.
 */

import { isDynamicBlockAuthoringRest } from "@dxf-ai-editor/drawing-model";

import type {
  DynamicBlockCommand,
  EditorCommand,
  TelemetryNavKind,
} from "./editor";

/** The full set of commands that flow through `pushAuditEvent`. */
export type AuditableCommand = EditorCommand | DynamicBlockCommand;

/**
 * Machine-groupable operation category. The aggregation key the CC-1a server
 * channel groups events on (ТЗ §14). Every command literal maps to exactly one
 * non-`"other"` category — enforced by `auditTaxonomy.test.ts`.
 */
export type AuditCategory =
  | "session"
  | "ui"
  | "layout"
  | "selection"
  | "lifecycle"
  | "property"
  | "commandline"
  | "create"
  | "viewport"
  | "modify"
  | "block"
  | "attribute"
  | "multileader"
  | "annotation"
  | "constraint"
  | "header"
  | "history"
  | "space"
  | "file"
  | "system"
  | "dynamic"
  | "geometry"
  | "view-op"
  // COLLAB-LOCK-COMMAND — the LOCK/UNLOCK soft-lock CLI commands.
  | "collab"
  // CC-1e — non-command interaction categories (ТЗ §14 "clicks"). Each mirrors
  // a `TelemetryEventKind` 1:1 so the transport can recover the wire kind from
  // the typed `params.category` (the typed source of truth) without a fragile
  // commandType string match.
  | "ui-click"
  | "ui-toggle"
  | "ui-nav"
  | "other";

/**
 * Pure view operations — they mutate no geometry and never pass through
 * `geometryShellReducer`. Their CC-1c emitters (App.tsx ZOOM/PAN/REGEN
 * routing + the `recordViewOp` store action) build the payload here.
 */
export const VIEW_OP_TYPES = ["pan", "zoom", "regen", "regenall"] as const;
export type ViewOpType = (typeof VIEW_OP_TYPES)[number];

// ── CC-1c — typed audit payload shapes (NO string bag) ─────────────────────
//
// `describeAuditOperation` returns one of these typed shapes keyed on the
// command type (ПРИОРИТЕТ #1 — typed structures, not a `Record<string,scalar>`
// bag). The flat `telemetryParamsSchema` (contracts/editor.ts) is ONLY the
// serialised WIRE projection; `telemetryServerChannel` projects typed→flat at
// the POST boundary. Every field is a JSON scalar so that wire projection is a
// pure structural widening.

/** JSON scalar permitted in a typed audit payload field. */
export type AuditParamScalar = string | number | boolean | null;

/** Discriminants every typed audit payload carries (the CC-1a aggregation keys). */
interface AuditParamsBase {
  /** Machine-groupable category — the primary aggregation key. */
  category: AuditCategory;
  /** The command/op literal the payload describes — the aggregation sub-key. */
  op: string;
}

export interface CreateMtextAuditParams extends AuditParamsBase {
  op: "geometry/create-mtext";
  spaceId: string;
  contentLength: number;
  styleName: string;
  nominalHeight: number;
  referenceWidth: number;
  insertionX: number;
  insertionY: number;
}

export interface ViewportSetLockAuditParams extends AuditParamsBase {
  op: "geometry/viewport-set-lock";
  viewportId: string;
  locked: boolean;
}

export interface ViewportSetShadePlotAuditParams extends AuditParamsBase {
  op: "geometry/viewport-set-shadeplot";
  viewportId: string;
  /**
   * DXF group 170 (0=As Displayed,1=Wireframe,2=Hidden,3=Rendered). `null` when
   * the command carries an ABSENT mode — the undo inverse that RESTORES an absent
   * group-170 record (`shadePlotMode: undefined`). Encoded as `null` (never
   * `undefined`) so the flat scalar telemetry/audit schema accepts it, mirroring
   * the `viewport-set-view` optional-field encoding.
   */
  shadePlotMode: number | null;
}

export interface ViewportVplayerAuditParams extends AuditParamsBase {
  op: "geometry/viewport-vplayer";
  viewportId: string;
  vplayerAction: "freeze" | "thaw";
  layerName: string;
}

export interface ViewportClipSetAuditParams extends AuditParamsBase {
  op: "geometry/viewport-clip-set";
  viewportId: string;
  boundsX1: number;
  boundsY1: number;
  boundsX2: number;
  boundsY2: number;
}

export interface ViewportClipSetPolygonAuditParams extends AuditParamsBase {
  op: "geometry/viewport-clip-set-polygon";
  viewportId: string;
  /**
   * Vertex count of an explicit-points polygon boundary (VPCLIP Polygonal /
   * scripted points), or `null` when the clip was authored from a selected
   * boundary OBJECT (`boundaryEntityId`) — the resolved vertex count is not
   * known at the command layer. Flat scalar (never `undefined`) so the
   * telemetry/audit schema accepts it, mirroring viewport-set-view.
   */
  boundaryVertexCount: number | null;
  /** Source boundary entity id (Select clipping object), or `null` for points. */
  boundaryEntityId: string | null;
}

export interface ViewportClipRemoveAuditParams extends AuditParamsBase {
  op: "geometry/viewport-clip-remove";
  viewportId: string;
}

export interface ViewportSetScaleAuditParams extends AuditParamsBase {
  op: "geometry/viewport-set-scale";
  viewportId: string;
  scaleNum: number;
  scaleDenom: number;
}

export interface ViewportSetViewAuditParams extends AuditParamsBase {
  op: "geometry/viewport-set-view";
  viewportId: string;
  viewHeight: number | null;
  viewCenterX: number | null;
  viewCenterY: number | null;
}

export interface TrimEntityAuditParams extends AuditParamsBase {
  op: "geometry/trim-entity";
  entityId: string;
  cuttingEdgeCount: number;
}

export interface ExtendEntityAuditParams extends AuditParamsBase {
  op: "geometry/extend-entity";
  entityId: string;
  boundaryEdgeCount: number;
}

export interface PropertiesApplyAuditParams extends AuditParamsBase {
  op: "properties/apply";
  entityCount: number;
  propertyCount: number;
}

/**
 * Fallback payload for command families without an explicit arm. Carries the
 * discriminants plus whatever well-known scalar fields the command exposes.
 */
export interface GenericAuditParams extends AuditParamsBase {
  entityId?: string;
  entityCount?: number;
  targetCount?: number;
  spaceId?: string;
  viewportId?: string;
}

/**
 * Op-specific scalar context a view-op emitter may attach. Typed (NOT a
 * `Record<string,scalar>` bag): the known view-op detail axes are enumerated.
 */
export interface ViewOpDetail {
  /** ZOOM sub-mode: "extents" | "all" | "window" | "previous" | "object" | … */
  mode?: string;
  /** PAN displacement in drawing units. */
  deltaX?: number;
  deltaY?: number;
  /** ZOOM scale factor (relative / nX / nXP). */
  factor?: number;
}

/** Pure view operation payload (REGEN/REGENALL/ZOOM/PAN). */
export interface ViewOpAuditParams extends ViewOpDetail {
  category: "view-op";
  op: ViewOpType;
}

// ── CC-1e — non-command interaction payloads (ТЗ §14 "clicks") ─────────────
//
// Typed discriminated payloads (NO string bag) for the non-command telemetry
// family. Each carries `category` (mirroring the wire `kind`) + a stable `op`
// family literal + its own typed fields. The op is the coarse aggregation
// family; the granular fields (controlId / setting / from→to) are the §14
// detail the CC-1d dashboard groups on.

/** A UI control was clicked (toolbar / ribbon / quickbar / panel control). */
export interface UiClickAuditParams {
  category: "ui-click";
  op: "ui/click";
  /** Surface the control lives on ("ribbon" / "quickbar" / "toolbar" / "panel"). */
  target: string;
  /** Stable control identifier ("quickbar-undo", "ribbon-action-LINE", …). */
  controlId: string;
}

/** A drawing-aid setting was toggled (OSNAP / ORTHO / POLAR / SNAP / GRID). */
export interface UiToggleAuditParams {
  category: "ui-toggle";
  op: "ui/toggle";
  /** Sysvar name of the toggled aid ("ORTHOMODE", "OSMODE", "POLARMODE", …). */
  setting: string;
  /** Resolved enabled state AFTER the toggle. */
  enabled: boolean;
}

/** A non-command navigation between surfaces (panel / tab / ribbon-tab / space). */
export interface UiNavAuditParams {
  category: "ui-nav";
  op: "ui/nav";
  navKind: TelemetryNavKind;
  from: string;
  to: string;
}

/** Union of the CC-1e non-command interaction payloads. */
export type UiInteractionAuditParams =
  | UiClickAuditParams
  | UiToggleAuditParams
  | UiNavAuditParams;

/**
 * Site-attached runtime-context extras a `pushAuditEvent` caller may layer on
 * top of the command-derived base — today ONLY the history-traversal markers
 * (the undo/redo sites read the reversed/re-applied command type + the new
 * head index from the store at dispatch time; they are not on the command
 * itself). Deliberately CANNOT carry `category`/`op`: those discriminants come
 * only from the taxonomy, and `pushAuditEvent` spreads the command-derived
 * base LAST so extras can never override them.
 */
export interface AuditExtraParams {
  reversedCommandType?: string;
  reappliedCommandType?: string;
  headIndex?: number;
}

/** Typed history-traverse arm (UNDO/REDO) — the resolved shape of base+extras. */
export interface HistoryTraverseAuditParams extends AuditParamsBase {
  category: "history";
  op: "history/undo" | "history/redo";
  reversedCommandType?: string;
  reappliedCommandType?: string;
  headIndex: number;
}

/**
 * The typed payload `describeAuditOperation` returns — a union keyed on the
 * command type. This is the in-code payload TYPE (ПРИОРИТЕТ #1); it is widened
 * to the flat `telemetryParamsSchema` wire shape only at the POST boundary.
 */
export type AuditOperationParams =
  | CreateMtextAuditParams
  | ViewportSetLockAuditParams
  | ViewportSetShadePlotAuditParams
  | ViewportVplayerAuditParams
  | ViewportClipSetAuditParams
  | ViewportClipSetPolygonAuditParams
  | ViewportClipRemoveAuditParams
  | ViewportSetScaleAuditParams
  | ViewportSetViewAuditParams
  | TrimEntityAuditParams
  | ExtendEntityAuditParams
  | PropertiesApplyAuditParams
  | ViewOpAuditParams
  | UiClickAuditParams
  | UiToggleAuditParams
  | UiNavAuditParams
  | HistoryTraverseAuditParams
  | GenericAuditParams;

/** Geometry modify verbs (transform/edit existing entities). */
const GEOMETRY_MODIFY_OPS = new Set<string>([
  "move-entities",
  "copy",
  "copy-entities",
  "rotate-entities",
  "scale-entities",
  "mirror-entities",
  "stretch-entities",
  "offset-entity",
  "trim-entity",
  "extend-entity",
  "fillet-entities",
  "chamfer-entities",
  "break-entity",
  "join-entities",
  "explode-entities",
  "explode-insert",
  "lengthen-entity",
  "arrayedit",
]);

/** Categorise the `geometry/<rest>` sub-domain. */
function categorizeGeometry(rest: string): AuditCategory {
  // PR #324 / #318 R2 — system-only inverse half of a delete that cleared a
  // dangling viewport clip. It's a viewport op, but `restore-viewport-clip`
  // does not match the `viewport-` PREFIX check below, so categorise it first.
  if (rest === "restore-viewport-clip") return "viewport";
  // @codex PR #785 round 2, F2 — the `properties/apply` PRESENCE-BIT restore is
  // an in-place, full-snapshot REPLACE of an entity that EXISTS both before and
  // after: only its field values (and the presence bits of its optionals)
  // change. That is the `modify` contract, NOT `lifecycle` — `lifecycle` is
  // reserved for the EXISTENCE transitions `delete-entity` / `restore-entity`
  // below, and an aggregation that folded a field restore into it would count a
  // property undo as an entity coming and going. `viewport` would be the same
  // mis-filing this command was introduced to end: the snapshot is an ARBITRARY
  // entity (the defect fixture is a heavy POLYLINE). The category is a pure
  // observability key — the CC-1a aggregation sub-key and the admin panel's
  // localized label (`auditPayloadPresentation.AUDIT_CATEGORY_LABEL_KEYS`,
  // which already carries `modify`) — so nothing but grouping and a label
  // depends on it. Ordered before the generic checks; the bare fallthrough
  // would say `geometry`, which is the taxonomy's "uncategorised geometry op"
  // bucket rather than a statement about what the command does.
  if (rest === "restore-entity-snapshot") return "modify";
  if (rest.startsWith("viewport-")) return "viewport";
  if (rest.startsWith("create-")) return "create";
  if (rest === "delete-entity" || rest === "restore-entity") return "lifecycle";
  if (GEOMETRY_MODIFY_OPS.has(rest)) return "modify";
  if (rest.includes("multileader")) return "multileader";
  if (
    rest.includes("attdef") ||
    rest.includes("attribute") ||
    rest === "attsync"
  )
    return "attribute";
  if (rest.includes("constraint")) return "constraint";
  if (rest === "annoupdate" || rest.startsWith("objectscale-"))
    return "annotation";
  // Dynamic-block authoring (shared one-truth predicate, incl. the `bv*`
  // visibility toggles) OR a per-instance `*insert*` override — both are the
  // audit `block` category here (the audit grouping folds instance overrides
  // into block; ABB-S1's bump classifier splits them out separately).
  if (isDynamicBlockAuthoringRest(rest) || rest.includes("insert"))
    return "block";
  return "geometry";
}

/**
 * Map a command literal to its audit category. Total over every literal in
 * `editorCommandSchema` + `dynamicBlockCommandSchema`; only an unrecognised
 * DOMAIN (a future command added without a category) returns `"other"`, which
 * the coverage test forbids.
 */
export function categoryForCommandType(commandType: string): AuditCategory {
  const slash = commandType.indexOf("/");
  const domain = slash === -1 ? commandType : commandType.slice(0, slash);
  const rest = slash === -1 ? "" : commandType.slice(slash + 1);
  switch (domain) {
    case "session":
      return "session";
    case "selection":
      return "selection";
    case "matchprop":
    case "chprop":
    case "properties":
      return "property";
    case "commandline":
      return "commandline";
    case "header":
      return "header";
    case "history":
      return "history";
    case "space":
      return "space";
    case "file":
      return "file";
    case "system":
      return "system";
    case "dynamic":
      return "dynamic";
    // collab/lock-request, collab/lock-release, collab/list-locks
    case "collab":
      return "collab";
    // entity/add-annotation-context, entity/remove-annotation-context
    case "entity":
      return "annotation";
    case "shell":
      if (rest === "open-panel") return "ui";
      if (rest === "select-entities" || rest === "clear-selection")
        return "selection";
      if (rest === "delete-entities" || rest === "restore-entities")
        return "lifecycle";
      // activate-space / rename-space / set-layout-context
      return "layout";
    case "geometry":
      return categorizeGeometry(rest);
    default:
      return "other";
  }
}

/**
 * Best-effort enrichment for command families that do NOT have an explicit
 * arm below. Reads only the well-known scalar/collection fields via `unknown`
 * narrowing (never `any`), so the result is a typed scalar map, not a string
 * bag. `category` + `op` are always present.
 */
function enrichGeneric(
  category: AuditCategory,
  command: AuditableCommand,
): GenericAuditParams {
  const params: GenericAuditParams = { category, op: command.type };
  const c = command as {
    entityId?: unknown;
    entityIds?: unknown;
    targetEntityIds?: unknown;
    spaceId?: unknown;
    viewportId?: unknown;
  };
  if (typeof c.entityId === "string") params.entityId = c.entityId;
  if (Array.isArray(c.entityIds)) params.entityCount = c.entityIds.length;
  if (Array.isArray(c.targetEntityIds))
    params.targetCount = c.targetEntityIds.length;
  if (typeof c.spaceId === "string") params.spaceId = c.spaceId;
  if (typeof c.viewportId === "string") params.viewportId = c.viewportId;
  return params;
}

/**
 * Project a command into its typed audit payload. Explicit arms enrich the
 * CC-1c emitter axes (create-mtext, the per-viewport ops, trim/extend,
 * properties/apply — which also backs the grip-drag commit) with their
 * salient scalar fields; every other command gets a categorised generic
 * payload. Geometry is never read for mutation — values are copied verbatim.
 */
export function describeAuditOperation(
  command: AuditableCommand,
): AuditOperationParams {
  const category = categoryForCommandType(command.type);
  switch (command.type) {
    case "geometry/create-mtext":
      return {
        category,
        op: command.type,
        spaceId: command.spaceId,
        contentLength: command.contents.length,
        styleName: command.styleName,
        nominalHeight: command.nominalHeight,
        referenceWidth: command.referenceWidth,
        insertionX: command.insertionPoint.x,
        insertionY: command.insertionPoint.y,
      };
    case "geometry/viewport-set-lock":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
        locked: command.locked,
      };
    case "geometry/viewport-set-shadeplot":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
        // `?? null` — an absent mode (the undo inverse restoring an absent
        // group-170 record) encodes as null, never undefined, so the flat scalar
        // audit/telemetry schema accepts it (mirrors viewport-set-view).
        shadePlotMode: command.shadePlotMode ?? null,
      };
    case "geometry/viewport-vplayer":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
        vplayerAction: command.action,
        layerName: command.layerName,
      };
    case "geometry/viewport-clip-set":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
        boundsX1: command.bounds.x1,
        boundsY1: command.bounds.y1,
        boundsX2: command.bounds.x2,
        boundsY2: command.bounds.y2,
      };
    case "geometry/viewport-clip-set-polygon":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
        // `?? null` — an object-sourced clip carries no explicit vertex list at
        // the command layer, so its count is null (never undefined) for the flat
        // scalar audit/telemetry schema (mirrors viewport-set-view).
        boundaryVertexCount: command.boundary?.length ?? null,
        boundaryEntityId: command.boundaryEntityId ?? null,
      };
    case "geometry/viewport-clip-remove":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
      };
    case "geometry/viewport-set-scale":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
        scaleNum: command.scaleNum,
        scaleDenom: command.scaleDenom,
      };
    case "geometry/viewport-set-view":
      return {
        category,
        op: command.type,
        viewportId: command.viewportId,
        viewHeight: command.viewHeight ?? null,
        viewCenterX: command.viewCenter?.x ?? null,
        viewCenterY: command.viewCenter?.y ?? null,
      };
    case "geometry/trim-entity":
      return {
        category,
        op: command.type,
        entityId: command.entityId,
        cuttingEdgeCount: command.cuttingEdgeIds.length,
      };
    case "geometry/extend-entity":
      return {
        category,
        op: command.type,
        entityId: command.entityId,
        boundaryEdgeCount: command.boundaryEdgeIds.length,
      };
    case "properties/apply":
      return {
        category,
        op: command.type,
        entityCount: command.entityIds.length,
        propertyCount: Object.keys(command.values).length,
      };
    default:
      return enrichGeneric(category, command);
  }
}

/**
 * The AFFECTED entity ids of a command — the per-action audit EVIDENCE stamped
 * on the ring entry (`AuditEvent.entityIds`) and persisted to the admin
 * `audit_events.entity_ids` column. Read VERBATIM from the source command's own
 * id field(s) (never inferred) — the full set of affected-entity id fields that
 * appear across `editorCommandSchema` + `dynamicBlockCommandSchema`:
 *   • `entityIds` — a multi-entity list (properties/apply, chprop/apply,
 *     shell/select-entities, move / mirror / rotate / scale / array …);
 *   • `sourceEntityIds` — a multi-entity SOURCE list (`geometry/copy`, and the
 *     `geometry/create-block-definition` convert/delete/retain source set);
 *   • `targetEntityIds` — matchprop targets (the entities the source properties
 *     are copied ONTO — the modified ones);
 *   • `entityId1` + `entityId2` — the PAIRED operands of `geometry/fillet-
 *     entities` / `geometry/chamfer-entities` (both are modified);
 *   • `entityId` — a single target (trim / extend / offset / dynamic-block ops);
 *   • `sourceEntityId` — a single SOURCE object (REVCLOUD Object-mode converts
 *     the picked object into the revision cloud);
 *   • `viewportId` — the modified PVIEWPORT of a viewport-edit command
 *     (`geometry/viewport-set-lock` / `-scale` / `-view` / `-shadeplot` /
 *     `-vplayer` / `-clip-set` / `-clip-remove`), which carries the affected
 *     entity as `viewportId` (`findViewportById` matches `entity.id ===
 *     viewportId`), never `entityId` / `sourceEntityId`;
 *   • `targetInsertIds` — the ATTSYNC (`geometry/attsync`) subset of the block's
 *     INSERTs to re-sync (the modified inserts); its `blockName` names the
 *     definition, not a drawing entity, so the target-insert list is the only
 *     affected-entity id source (when omitted every INSERT is synced but no id
 *     list is supplied, so the row falls through to `[]`);
 *   • `insertId` — the single modified INSERT of the block/insert edits
 *     (`geometry/explode-insert`, `geometry/update-attribute-value(s)`,
 *     `space/enter-refedit`, `geometry/objectscale-add` / `-remove`).
 * Returns `[]` for a command that touches no entity (a view-op / session /
 * layout op). Reference-only ids are DELIBERATELY excluded (never affected):
 * `pathEntityId` (arraypath path is guide geometry; the arrayed objects live in
 * `entityIds`), `invisibleEntityIds` (a visibility-STATE config list whose
 * subject is the block, keyed by `blockName`, not a drawing-edit set), and
 * `arrayGroupId` (the `geometry/arrayedit` associative-array MEMBERSHIP tag — a
 * group key shared by many entities, NOT a selectable/persisted drawing entity
 * id; ARRAYEDIT carries no member id list at projection time, so its row
 * honestly persists empty entity_ids rather than corrupting them with a
 * non-entity identifier — array-group evidence belongs in a dedicated field).
 *
 * This is the SOURCE of the audit-record `entityIds`. Without it the multi-entity
 * commands lose their ids after `describeAuditOperation`: the typed `params`
 * carries only the aggregate `entityCount` (the scalar telemetry aggregation
 * key), which a scalar-only `params` bag cannot widen to the actual id LIST — so
 * the audit projection previously wrote `entityIds: []` for every selection /
 * CHPROP / apply, and for the paired FILLET/CHAMFER + source-set COPY/BLOCK
 * commands whose ids live outside the three standard names. Uses `unknown`
 * narrowing (never `any`) so it reads only the well-known id fields off any
 * command shape.
 */
export function affectedEntityIdsForCommand(
  command: AuditableCommand,
): string[] {
  const c = command as {
    entityId?: unknown;
    entityIds?: unknown;
    sourceEntityIds?: unknown;
    targetEntityIds?: unknown;
    targetInsertIds?: unknown;
    entityId1?: unknown;
    entityId2?: unknown;
    sourceEntityId?: unknown;
    viewportId?: unknown;
    insertId?: unknown;
  };
  const asIdList = (value: unknown): string[] =>
    Array.isArray(value)
      ? value.filter((v): v is string => typeof v === "string" && v.length > 0)
      : [];
  const asId = (value: unknown): string | null =>
    typeof value === "string" && value.length > 0 ? value : null;
  // Multi-entity lists first (the finding: a selection / apply / CHPROP / copy /
  // block-def source-set carries the whole list, not a single id), then the
  // matchprop targets, then the ATTSYNC target-insert subset.
  const multi = asIdList(c.entityIds);
  if (multi.length > 0) return multi;
  const sources = asIdList(c.sourceEntityIds);
  if (sources.length > 0) return sources;
  const targets = asIdList(c.targetEntityIds);
  if (targets.length > 0) return targets;
  // ATTSYNC (`geometry/attsync`) restricts the sync to a subset of the block's
  // INSERTs via `targetInsertIds` — the MODIFIED inserts. When present these ARE
  // the affected entities (the command's only entity ids; its `blockName` names
  // the definition, not a drawing entity). When absent every INSERT of the block
  // is synced, but the command carries no id list to stamp, so it falls through.
  const targetInserts = asIdList(c.targetInsertIds);
  if (targetInserts.length > 0) return targetInserts;
  // Paired two-operand ops (FILLET / CHAMFER) — both operands are modified.
  const pair = [asId(c.entityId1), asId(c.entityId2)].filter(
    (v): v is string => v !== null,
  );
  if (pair.length > 0) return pair;
  // Single-target / single-source ops, then the viewport-edit family, then the
  // single-INSERT ops. A viewport-edit command (geometry/viewport-set-lock /
  // -scale / -view / -shadeplot / -vplayer / -clip-set / -clip-remove) carries
  // the MODIFIED drawing entity — the PVIEWPORT itself — as `viewportId`
  // (findViewportById matches `entity.id === viewportId`), never `entityId` /
  // `sourceEntityId`. `insertId` is the single modified INSERT of the
  // block/insert edits (geometry/explode-insert, geometry/update-attribute-
  // value(s), space/enter-refedit, geometry/objectscale-add / -remove).
  //
  // `arrayGroupId` (geometry/arrayedit) is DELIBERATELY NOT an id source: it is
  // the associative-array MEMBERSHIP tag shared by many entities (the reducer
  // resolves the actual members from it), not a selectable/persisted drawing
  // entity id. Stamping it into `entityIds` would corrupt the entity-level audit
  // evidence — the admin detail renders `entityIds` as affected entities and
  // would then point at a non-entity. ARRAYEDIT carries no member id list at
  // projection time, so its row honestly persists empty entity_ids; array-group
  // evidence belongs in a dedicated group-id field, never in `entityIds`. (The
  // array CREATE ops still carry `entityIds` — the arrayed source objects — and
  // win in the multi-list branch above.)
  const single =
    asId(c.entityId) ??
    asId(c.sourceEntityId) ??
    asId(c.viewportId) ??
    asId(c.insertId);
  if (single !== null) return [single];
  return [];
}

/**
 * Build the typed payload for a view operation (REGEN/REGENALL/ZOOM/PAN). The
 * resulting `category` is always `"view-op"`; the caller passes `opType` as the
 * audit event's `commandType` so the transport classifies it correctly.
 */
export function describeViewOpOperation(
  opType: ViewOpType,
  detail?: ViewOpDetail,
): ViewOpAuditParams {
  return { category: "view-op", op: opType, ...(detail ?? {}) };
}

// ── CC-1e — non-command interaction payload builders ───────────────────────
//
// These mirror `describeViewOpOperation`: they build the typed audit payload
// for an interaction that does NOT pass through the command schema. The store
// `recordUiClick` / `recordUiToggle` / `recordUiNav` actions call these and
// route the result through `pushUiInteractionAuditEvent`.

/** Build the typed payload for a UI click (ТЗ §14 "clicks"). */
export function describeUiClickOperation(input: {
  target: string;
  controlId: string;
}): UiClickAuditParams {
  return {
    category: "ui-click",
    op: "ui/click",
    target: input.target,
    controlId: input.controlId,
  };
}

/** Build the typed payload for a drawing-aid toggle. */
export function describeUiToggleOperation(input: {
  setting: string;
  enabled: boolean;
}): UiToggleAuditParams {
  return {
    category: "ui-toggle",
    op: "ui/toggle",
    setting: input.setting,
    enabled: input.enabled,
  };
}

/** Build the typed payload for a non-command navigation. */
export function describeUiNavOperation(input: {
  navKind: TelemetryNavKind;
  from: string;
  to: string;
}): UiNavAuditParams {
  return {
    category: "ui-nav",
    op: "ui/nav",
    navKind: input.navKind,
    from: input.from,
    to: input.to,
  };
}
